import { get } from 'svelte/store'

import { sdk } from '@lib/sdk/sdk'

import { type TSuiObject } from '@app/types'
import { suiPackageStore } from '../../../stores'
import { SUI_NATIVE_DECIMALS } from '@src/constants/blockchain'
import { initSuiClient } from '@src/libs/sui'
import { configStore } from '@src/stores/config'

export const getSuiObject = async (objectId: string): Promise<TSuiObject> => {
  const suiClient = await sdk.sui.getSuiClient()
  const res = await suiClient.getObject({
    id: objectId,
    options: {
      showType: true,
      showOwner: true,
      showContent: true,
    },
  })

  return res.data
}

const isLegacyAssetPriceFiatError = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false

  return (
    err.message.includes('INVALID_ARGUMENT') ||
    err.message.includes('FIELD_MISSING') ||
    err.message.includes('ListDynamicFields')
  )
}

export const getAssetPrice = async (
  packageId: string,
  assetId: string,
  currency = 'USD',
): Promise<[number, number]> => {
  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(packageId, suiNetwork)

  let priceFiat: number

  try {
    priceFiat = await client.CredenzaAssetCollection.getAssetPriceFiat(+assetId, currency)
  } catch (err) {
    const canFallbackToLegacyUsd = currency === 'USD' && isLegacyAssetPriceFiatError(err)

    if (!canFallbackToLegacyUsd) {
      throw err
    }

    priceFiat = await client.CredenzaAssetCollection.getAssetPriceFiat(+assetId)
  }

  const priceCoin = await client.CredenzaAssetCollection.getAssetPriceCoin(+assetId)

  return [priceFiat, priceCoin]
}

export const getSuiCredCoinType = () => `${get(suiPackageStore)}::cred::CRED`
export const getSuiCoinType = () => '0x2::sui::SUI'

export const getSuiLoyaltyCoinType = () => `${get(suiPackageStore)}::credenza_loyalty::CREDENZA_LOYALTY`

export const getSellableConfigId = async (packageId: string): Promise<string> => {
  const { suiNetwork } = get(configStore)
  const client = await initSuiClient(packageId, suiNetwork)

  return (await client.graphqlClient.findSharedObjectByType(`${packageId}::sellable::CredenzaSellableConfig`)) as string
}

export const convertSuiMicroBalance = (balance: string | number, decimals = SUI_NATIVE_DECIMALS): string => {
  return String(Number(balance) / decimals)
}
